import { useEffect, useMemo, useState } from 'react';
import {
  STICKER_IMPORT_BATCH_SIZE,
  importStickerEntries,
  isDcinsideSource,
  loadAdminStickerPack,
  loadAdminStickerPacks,
  parseStickerCandidates,
  saveActiveStickerIds,
  saveStickerPackSettings,
} from './stickerAdminApi.js';
import { invalidateStickerCache, stickerAssetUrl } from '../stickerApi.js';

function CandidatePreview({ entry }) {
  // 디시 이미지 서버는 gall.dcinside.com 레퍼러가 아니면 403 이라 여기서는 못 띄운다.
  // 저장할 때 워커가 레퍼러를 붙여 받아 오므로, 저장 후 목록에서는 보인다.
  if (isDcinsideSource(entry.sourceUrl)) return <span className="platform-sticker-preview-empty">디시콘<br />저장 후 표시</span>;
  const preview = entry.posterUrl || entry.sourceUrl;
  if (entry.mediaType === 'video' && !entry.posterUrl) {
    return <video src={entry.sourceUrl} muted playsInline preload="metadata" />;
  }
  return <img src={preview} alt="" loading="lazy" />;
}

function StoredPreview({ sticker }) {
  const preview = sticker.posterUrl || sticker.mediaUrl;
  const url = stickerAssetUrl(preview);
  if (!url) return <span className="platform-sticker-preview-empty">미리보기 없음</span>;
  return <img src={url} alt="" loading="lazy" />;
}

const SURFACE_LABEL = { control: '관제 채팅', broadcast: '중계방' };

export function PlatformStickerManagementBoard({ mobile = false }) {
  const [packId, setPackId] = useState('');
  const [name, setName] = useState('');
  const [html, setHtml] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState(() => new Set());
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState('');
  const [storedPack, setStoredPack] = useState(null);
  const [activeStickerIds, setActiveStickerIds] = useState(() => new Set());
  // 격자는 한 번에 한 채팅만 편집한다. 두 벌을 동시에 보여 주면 317개 썸네일에 체크박스가
  // 두 개씩 붙어 무엇을 보고 있는지 알 수 없다.
  const [surface, setSurface] = useState('control');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [notice, setNotice] = useState({ type: '', message: '' });

  const selectedCandidates = useMemo(
    () => candidates.filter((entry) => selectedCandidateIds.has(entry.dataId)),
    [candidates, selectedCandidateIds],
  );
  const onSurface = (sticker, which) => String(sticker.surfaces ?? 'control,broadcast').split(',').includes(which);
  // 팩도 채팅마다 따로 멈춘다. 옛 전체 중지(active=false)면 양쪽 다 멈춘 것으로 본다.
  const packOn = (pack, which) => Boolean(pack?.active) && String(pack?.surfaces ?? 'control,broadcast').split(',').includes(which);
  const savedActiveIds = useMemo(
    () => new Set((storedPack?.stickers || []).filter((sticker) => onSurface(sticker, surface)).map((sticker) => sticker.id)),
    [storedPack, surface],
  );
  const hasActiveChanges = useMemo(() => {
    if (!storedPack) return false;
    if (savedActiveIds.size !== activeStickerIds.size) return true;
    for (const id of activeStickerIds) if (!savedActiveIds.has(id)) return true;
    return false;
  }, [activeStickerIds, savedActiveIds, storedPack]);

  // 채팅을 바꾸면 격자를 그 채팅의 저장값으로 다시 맞춘다. 안 그러면 관제에서 찍은 체크가
  // 중계 탭에 그대로 남아, 저장 한 번에 중계 설정을 관제 것으로 덮어쓴다.
  useEffect(() => { setActiveStickerIds(new Set(savedActiveIds)); }, [surface, storedPack]);

  const refreshPacks = async ({ preferPackId = '' } = {}) => {
    const result = await loadAdminStickerPacks();
    const next = result.packs || [];
    setPacks(next);
    const wanted = preferPackId && next.some((pack) => pack.id === preferPackId)
      ? preferPackId
      : selectedPackId && next.some((pack) => pack.id === selectedPackId)
        ? selectedPackId
        : next[0]?.id || '';
    setSelectedPackId(wanted);
    return wanted;
  };

  const loadPack = async (id) => {
    if (!id) {
      setStoredPack(null);
      setActiveStickerIds(new Set());
      return;
    }
    const result = await loadAdminStickerPack(id);
    setStoredPack(result);
    setActiveStickerIds(new Set((result.stickers || []).filter((sticker) => onSurface(sticker, surface)).map((sticker) => sticker.id)));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadAdminStickerPacks()
      .then((result) => {
        if (!active) return;
        const next = result.packs || [];
        setPacks(next);
        setSelectedPackId(next[0]?.id || '');
      })
      .catch((error) => {
        if (active) setNotice({ type: 'error', message: error instanceof Error ? error.message : '콘팩 목록을 불러오지 못했습니다.' });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (!selectedPackId) {
      setStoredPack(null);
      return undefined;
    }
    setLoading(true);
    loadAdminStickerPack(selectedPackId)
      .then((result) => {
        if (!active) return;
        setStoredPack(result);
        setActiveStickerIds(new Set((result.stickers || []).filter((sticker) => onSurface(sticker, surface)).map((sticker) => sticker.id)));
      })
      .catch((error) => {
        if (active) setNotice({ type: 'error', message: error instanceof Error ? error.message : '콘팩을 불러오지 못했습니다.' });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedPackId]);

  const analyze = () => {
    try {
      const parsed = parseStickerCandidates(html);
      setCandidates(parsed);
      setSelectedCandidateIds(new Set(parsed.map((entry) => entry.dataId)));
      setNotice({ type: 'success', message: `${parsed.length}개 콘을 찾았습니다. 가져올 콘만 체크한 뒤 저장하세요.` });
    } catch (error) {
      setCandidates([]);
      setSelectedCandidateIds(new Set());
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '콘 HTML 분석에 실패했습니다.' });
    }
  };

  const toggleCandidate = (dataId) => {
    setSelectedCandidateIds((current) => {
      const next = new Set(current);
      if (next.has(dataId)) next.delete(dataId);
      else next.add(dataId);
      return next;
    });
  };

  // 가져오기 버튼이 왜 꺼져 있는지 보여 준다. 콘팩 ID 칸의 회색 예시(placeholder)가 값처럼 보여서
  // ID 를 비워 둔 채 버튼만 눌러 보다 막혔다(2026-10-19).
  const trimmedPackId = packId.trim();
  const existingPack = packs.find((pack) => pack.id === trimmedPackId);
  const importBlockedReason = !trimmedPackId ? '콘팩 ID를 입력해야 가져올 수 있습니다.'
    : !/^[a-z0-9][a-z0-9_-]{0,63}$/.test(trimmedPackId) ? '콘팩 ID는 영문 소문자·숫자·-·_ 만 쓸 수 있습니다.'
      : !name.trim() ? '콘팩 이름을 입력해야 가져올 수 있습니다.' : '';

  const runImport = async () => {
    if (!selectedCandidates.length || importing) return;
    setImporting(true);
    setProgress({ current: 0, total: selectedCandidates.length });
    setNotice({ type: '', message: '' });
    const failures = [];
    try {
      for (let index = 0; index < selectedCandidates.length; index += STICKER_IMPORT_BATCH_SIZE) {
        const batch = selectedCandidates.slice(index, index + STICKER_IMPORT_BATCH_SIZE);
        const result = await importStickerEntries({ packId: packId.trim(), name: name.trim(), entries: batch });
        for (const item of result.results || []) {
          if (item.status === 'failed') failures.push(`${item.id}: ${item.error || 'failed'}`);
        }
        setProgress({ current: Math.min(index + batch.length, selectedCandidates.length), total: selectedCandidates.length });
      }
      const loadedPackId = await refreshPacks({ preferPackId: packId.trim() });
      await loadPack(loadedPackId);
      setSelectedPackId(loadedPackId);
      setNotice(failures.length
        ? { type: 'error', message: `${selectedCandidates.length - failures.length}개 저장, ${failures.length}개 실패했습니다. 실패 항목은 다시 실행하면 이어서 처리됩니다.` }
        : { type: 'success', message: `${selectedCandidates.length}개 콘을 저장했습니다.` });
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '콘 가져오기가 중단됐습니다. 다시 실행하면 저장된 항목은 건너뜁니다.' });
    } finally {
      setImporting(false);
    }
  };

  const saveActive = async () => {
    if (!storedPack?.pack?.id || saving) return;
    const targetPackId = storedPack.pack.id;
    const requestedIds = [...activeStickerIds].sort();
    setSaving(true);
    setNotice({ type: '', message: '' });
    try {
      await saveActiveStickerIds(targetPackId, requestedIds, surface);
      invalidateStickerCache('/v1/sticker-packs');
      invalidateStickerCache(`/v1/sticker-packs/${encodeURIComponent(targetPackId)}`);

      const verified = await loadAdminStickerPack(targetPackId);
      const verifiedIds = (verified.stickers || [])
        .filter((sticker) => onSurface(sticker, surface))
        .map((sticker) => sticker.id)
        .sort();
      if (verifiedIds.length !== requestedIds.length
        || verifiedIds.some((id, index) => id !== requestedIds[index])) {
        throw new Error(`저장 검증 실패: 요청 ${requestedIds.length}개 / 실제 ${verifiedIds.length}개`);
      }

      setStoredPack(verified);
      setActiveStickerIds(new Set(verifiedIds));
      await refreshPacks({ preferPackId: targetPackId });
      setNotice({ type: 'success', message: `${SURFACE_LABEL[surface]}에서 쓸 콘 ${verifiedIds.length}개가 실제 DB에 반영됐습니다.` });
    } catch (error) {
      try {
        const latest = await loadAdminStickerPack(targetPackId);
        setStoredPack(latest);
        setActiveStickerIds(new Set((latest.stickers || []).filter((sticker) => onSurface(sticker, surface)).map((sticker) => sticker.id)));
      } catch { /* keep current state if verification reload also fails */ }
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '콘 사용 설정을 저장하지 못했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const togglePack = async () => {
    if (!storedPack?.pack?.id || saving) return;
    setSaving(true);
    try {
      const nextOn = !packOn(storedPack.pack, surface);
      const result = await saveStickerPackSettings(storedPack.pack.id, { surface, surfaceActive: nextOn });
      invalidateStickerCache('/v1/sticker-packs');
      invalidateStickerCache(`/v1/sticker-packs/${encodeURIComponent(storedPack.pack.id)}`);
      setStoredPack(result);
      await refreshPacks({ preferPackId: storedPack.pack.id });
      setNotice({ type: 'success', message: packOn(result.pack, surface)
        ? `${SURFACE_LABEL[surface]}에서 콘팩을 다시 사용합니다.`
        : `${SURFACE_LABEL[surface]}에서만 콘팩을 중지했습니다. ${SURFACE_LABEL[surface === 'control' ? 'broadcast' : 'control']}은 그대로입니다.` });
    } catch (error) {
      setNotice({ type: 'error', message: error instanceof Error ? error.message : '콘팩 상태를 변경하지 못했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const toggleStored = (id) => {
    setActiveStickerIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return <section className={`platform-sticker-management ${mobile ? 'mobile' : 'desktop'}`} aria-label="플랫폼 콘 관리">
    <header>
      <div>
        <span className="platform-sticker-kicker">STICKER CONTROL</span>
        <h2>콘 관리</h2>
        <p>콘팩 가져오기와 채팅 노출 여부를 한 화면에서 관리합니다.</p>
      </div>
      <span className="platform-sticker-admin-badge">전체 관리자</span>
    </header>

    <section className="platform-sticker-import">
      <div className="platform-sticker-section-title">
        <div>
          <span>01</span>
          <div><h3>콘팩 가져오기</h3><p>HTML 분석 후 실제로 필요한 콘만 선택해 저장합니다.</p></div>
        </div>
        {candidates.length ? <strong>{selectedCandidates.length} / {candidates.length} 선택</strong> : null}
      </div>
      <div className="platform-sticker-import-fields">
        <label><span>콘팩 ID</span><input value={packId} maxLength={64} placeholder="예: dc-shudol-v5" onChange={(event) => setPackId(event.target.value.toLowerCase())} /></label>
        <label><span>콘팩 이름</span><input value={name} maxLength={100} placeholder="아카콘" onChange={(event) => setName(event.target.value)} /></label>
      </div>
      <label className="platform-sticker-html"><span>콘 HTML (아카콘 · 디시콘)</span><textarea value={html} onChange={(event) => setHtml(event.target.value)} rows={8} placeholder={'아카콘: 이모티콘 목록을 그대로 복사해 붙이세요. 디시콘: 디시콘 목록(dccon_list)을 그대로 복사해 붙이세요.'} /></label>
      <div className="platform-sticker-actions">
        <button type="button" className="secondary" onClick={analyze} disabled={importing}>HTML 분석</button>
        {candidates.length ? <>
          <button type="button" onClick={() => setSelectedCandidateIds(new Set(candidates.map((entry) => entry.dataId)))} disabled={importing}>전체 선택</button>
          <button type="button" onClick={() => setSelectedCandidateIds(new Set())} disabled={importing}>전체 해제</button>
          <button type="button" className="primary" onClick={runImport} disabled={importing || !selectedCandidates.length || Boolean(importBlockedReason)}>
            {importing ? `가져오는 중 ${progress.current}/${progress.total}` : `선택 ${selectedCandidates.length}개 가져오기`}
          </button>
          {importBlockedReason ? <small className="platform-sticker-import-hint">{importBlockedReason}</small>
            : existingPack ? <small className="platform-sticker-import-hint warn">이미 있는 '{existingPack.name}' 콘팩에 추가되고, 콘팩 이름이 '{name.trim()}'(으)로 바뀝니다.</small> : null}
        </> : null}
      </div>

      {importing ? <div className="platform-sticker-progress" aria-label="콘 가져오기 진행률">
        <span style={{ width: `${progress.total ? Math.round((progress.current / progress.total) * 100) : 0}%` }} />
      </div> : null}
      {candidates.length ? <div className="platform-sticker-grid import-grid">
        {candidates.map((entry) => <label className={selectedCandidateIds.has(entry.dataId) ? 'selected' : 'excluded'} key={entry.dataId}>
          <input type="checkbox" checked={selectedCandidateIds.has(entry.dataId)} onChange={() => toggleCandidate(entry.dataId)} aria-label={`${entry.dataId} 가져오기`} />
          <CandidatePreview entry={entry} />
          <span>{entry.dataId}</span>
          <small>{entry.mediaType === 'video' ? '동영상' : '이미지'}</small>
          {selectedCandidateIds.has(entry.dataId) ? null : <em>제외</em>}
        </label>)}
      </div> : null}
    </section>

    <section className="platform-sticker-existing">
      <div className="platform-sticker-existing-head">
        <div className="platform-sticker-section-title">
          <div>
            <span>02</span>
            <div><h3>사용 콘 관리</h3><p>채팅별로 따로 고릅니다. 체크된 콘만 그 채팅의 선택창과 전송 검증에서 사용됩니다.</p></div>
          </div>
        </div>
        {/* The summary right below owns the numbers, so the picker only has to name the pack. */}
        <label><span>콘팩</span><select value={selectedPackId} onChange={(event) => setSelectedPackId(event.target.value)}>
          {packs.length ? packs.map((pack) => <option key={pack.id} value={pack.id}>{pack.name}</option>) : <option value="">등록된 콘팩 없음</option>}
        </select></label>
      </div>

      {storedPack ? <>
        <div className="platform-sticker-surface-switch" role="group" aria-label="편집할 채팅">
          {['control', 'broadcast'].map((value) => {
            const count = (storedPack.stickers || []).filter((sticker) => onSurface(sticker, value)).length;
            return <button key={value} type="button" className={value === surface ? 'active' : ''} aria-pressed={value === surface}
              onClick={() => setSurface(value)} disabled={saving}>{SURFACE_LABEL[value]} <b>{count}</b>{packOn(storedPack.pack, value) ? null : <small>중지</small>}</button>;
          })}
          {hasActiveChanges ? <em>저장하지 않은 변경이 있습니다. 채팅을 바꾸면 사라집니다.</em> : null}
        </div>
        <div className="platform-sticker-pack-summary">
          <div className="platform-sticker-pack-identity">
            <div><strong>{storedPack.pack.name}</strong><span>{storedPack.pack.id}</span></div>
            <div className="platform-sticker-stats">
              <span><b>{activeStickerIds.size}</b> {SURFACE_LABEL[surface]} 사용</span>
              <span><b>{Math.max(0, (storedPack.stickers || []).length - activeStickerIds.size)}</b> 제외</span>
              <span><b>{(storedPack.stickers || []).length}</b> 전체</span>
            </div>
          </div>
          <div className="platform-sticker-pack-actions">
            <button type="button" onClick={() => setActiveStickerIds(new Set((storedPack.stickers || []).map((sticker) => sticker.id)))} disabled={saving}>전체 사용</button>
            <button type="button" onClick={() => setActiveStickerIds(new Set())} disabled={saving}>전체 제외</button>
            <button type="button" onClick={() => setActiveStickerIds(new Set((storedPack.stickers || [])
              .filter((sticker) => onSurface(sticker, surface === 'control' ? 'broadcast' : 'control'))
              .map((sticker) => sticker.id)))} disabled={saving}>{SURFACE_LABEL[surface === 'control' ? 'broadcast' : 'control']}과 동일하게</button>
            <button type="button" className={packOn(storedPack.pack, surface) ? 'danger-soft' : 'secondary'} onClick={togglePack} disabled={saving}>
              {packOn(storedPack.pack, surface) ? `${SURFACE_LABEL[surface]} 콘팩 중지` : `${SURFACE_LABEL[surface]} 콘팩 사용`}
            </button>
            <button type="button" className="primary" onClick={saveActive} disabled={saving || !hasActiveChanges}>
              {saving ? '저장·검증 중…' : hasActiveChanges ? `${SURFACE_LABEL[surface]} 저장 (${activeStickerIds.size})` : '저장됨'}
            </button>
          </div>
        </div>
        <div className="platform-sticker-grid stored-grid">
          {(storedPack.stickers || []).map((sticker) => {
            const enabled = activeStickerIds.has(sticker.id);
            // Nearly every 콘 is 사용, so that is the plain state and only 제외 gets a tag. Labelling
            // both put a chip on top of all 177 thumbnails and hid the very artwork being judged.
            return <label className={enabled ? 'selected' : 'excluded'} key={sticker.id}>
              <input type="checkbox" checked={enabled} onChange={() => toggleStored(sticker.id)} aria-label={`${sticker.dataId} ${SURFACE_LABEL[surface]} 사용`} />
              <StoredPreview sticker={sticker} />
              <span>{sticker.dataId}</span>
              <small>{sticker.mediaType === 'video' ? '동영상' : '이미지'} · {Math.max(1, Math.round(sticker.byteSize / 1024))}KB</small>
              {enabled ? null : <em>제외</em>}
            </label>;
          })}
        </div>
      </> : loading ? <p className="platform-sticker-empty">콘팩을 불러오는 중입니다.</p> : <p className="platform-sticker-empty">등록된 콘팩이 없습니다.</p>}
    </section>

    {notice.message ? <p className={`platform-sticker-notice ${notice.type}`} role="status">{notice.message}</p> : null}
  </section>;
}
